import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, BookOpen, CheckCircle2, ExternalLink, Info, Sparkles,
} from "lucide-react";
import {
  platformListProperties,
  platformListFeatureFlags,
  platformSubmitKnowledgeImport,
} from "../../lib/api";

/**
 * Sprint 5 — Import Knowledge from Message (Admin only).
 *
 * Flow:
 *   1. Admin picks one tenant-owned property.
 *   2. Admin pastes an Airbnb / Booking message.
 *   3. Submit → backend sanitizes → AI Gateway → strict schema → KB drafts.
 *   4. Result page lists created drafts with a link to the existing KB
 *      editor for review/edit/discard/publish.
 *
 * The page fails closed if the `knowledgeImportMvp` flag is off on this
 * tenant — even the direct route would 404. The parent nav already hides
 * the entry.
 */

const NOTICE_PII = (
  "Do not paste card numbers, IDs, or passport details. Sensitive access " +
  "information — Wi-Fi passwords, PINs, lockbox / door / alarm codes — is " +
  "automatically excluded before generation and must be managed through the " +
  "existing approved process."
);

const MAX_MESSAGE_CHARS = 12_000;

export default function ImportKnowledgePanel() {
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [featureOn, setFeatureOn] = useState(null); // null=loading, true/false

  useEffect(() => {
    (async () => {
      // Decouple the flag check from the properties fetch — a properties
      // failure must never mask the flag state. Both requests are made in
      // parallel but their outcomes are handled independently.
      const [propsRes, flagsRes] = await Promise.allSettled([
        platformListProperties({ limit: 100, offset: 0 }),
        platformListFeatureFlags(),
      ]);
      if (propsRes.status === "fulfilled") {
        setProperties(propsRes.value?.items || []);
      } else {
        setError(
          propsRes.reason?.response?.data?.message ||
          "Could not load properties.",
        );
      }
      if (flagsRes.status === "fulfilled") {
        const flag = (flagsRes.value || []).find((f) => f.key === "knowledgeImportMvp");
        setFeatureOn(!!flag?.enabled);
      } else {
        // Fail-closed on flag read errors.
        setFeatureOn(false);
      }
    })();
  }, []);

  const chars = message.length;
  const overLimit = chars > MAX_MESSAGE_CHARS;
  const tooShort = chars > 0 && chars < 20;

  const canSubmit = useMemo(
    () => featureOn && propertyId && chars >= 20 && !overLimit && !busy,
    [featureOn, propertyId, chars, overLimit, busy],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const data = await platformSubmitKnowledgeImport({
        propertyId,
        message: message.trim(),
      });
      setResult(data);
      if (data.state === "ready_for_review") {
        setMessage("");
      }
    } catch (e) {
      const body = e?.response?.data;
      setError(body?.message || "Import failed. Please try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  if (featureOn === false) {
    return (
      <div data-testid="import-knowledge-disabled" className="max-w-2xl">
        <h1 className="font-serif text-3xl">Import Knowledge from Message</h1>
        <div className="mt-6 p-4 border border-amber-300 bg-amber-50 rounded-sm text-sm">
          <AlertTriangle size={16} className="inline mr-2 text-amber-800" />
          This feature is not enabled for your tenant. Ask your platform
          operator to enable <code className="font-mono">knowledgeImportMvp</code>.
        </div>
      </div>
    );
  }

  return (
    <div data-testid="import-knowledge-panel">
      <header className="mb-7 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="ed-overline mb-2 flex items-center gap-2">
            <Sparkles size={12} strokeWidth={1.8} className="text-[var(--ed-emerald)]" />
            AI Import
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">
            Import Knowledge from Message
          </h1>
          <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5 max-w-2xl">
            Paste an Airbnb or Booking.com message. We&apos;ll extract only the
            facts the message explicitly supports and turn them into Knowledge
            Base <span className="font-medium">drafts</span> for your review.
            Nothing is published automatically. Nothing reaches the Concierge
            until you publish it.
          </p>
        </div>
      </header>

      <form onSubmit={submit} className="space-y-5 max-w-2xl">
        <div>
          <label htmlFor="ik-prop" className="ed-overline text-[0.55rem] mb-1.5 block">
            Property
          </label>
          <select
            id="ik-prop"
            data-testid="ik-property"
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            required
            className="w-full text-sm px-3 py-2.5 border border-[var(--ed-rule)] bg-white focus:outline-none focus:border-[var(--ed-ink)] rounded-sm"
          >
            <option value="">Select a property…</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} · {p.status}
              </option>
            ))}
          </select>
          {properties.length === 0 && (
            <p className="text-xs text-[var(--ed-ink-muted)] mt-1">
              No properties yet. Create one from <b>Platform Properties</b> first.
            </p>
          )}
        </div>

        <div>
          <label htmlFor="ik-msg" className="ed-overline text-[0.55rem] mb-1.5 block">
            Paste an Airbnb or Booking.com message
          </label>
          <textarea
            id="ik-msg"
            data-testid="ik-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={10}
            maxLength={MAX_MESSAGE_CHARS}
            aria-describedby="ik-privacy"
            placeholder="Hi guest, check-in is available from 3pm; please leave the residence by 11am on the day of departure…"
            className="w-full text-sm px-3 py-2.5 border border-[var(--ed-rule)] bg-white focus:outline-none focus:border-[var(--ed-ink)] rounded-sm font-mono"
          />
          <div className="flex justify-between items-start text-[0.7rem] text-[var(--ed-ink-muted)] mt-1.5">
            <span id="ik-privacy" className="max-w-[520px]">{NOTICE_PII}</span>
            <span className={overLimit ? "text-red-700 font-medium" : ""} data-testid="ik-char-count">
              {chars.toLocaleString()} / {MAX_MESSAGE_CHARS.toLocaleString()}
            </span>
          </div>
          {tooShort && (
            <p className="text-xs text-amber-800 mt-1" role="status">
              Please paste at least 20 characters to extract knowledge from.
            </p>
          )}
        </div>

        <button
          type="submit"
          data-testid="ik-submit"
          disabled={!canSubmit}
          className="text-[0.72rem] uppercase tracking-[0.22em] py-3 px-6 bg-[var(--ed-ink)] text-white hover:bg-[var(--ed-emerald)] rounded-sm disabled:opacity-40 flex items-center gap-2"
        >
          <Sparkles size={13} />
          {busy ? "Generating drafts…" : "Generate Knowledge Base Drafts"}
        </button>

        <div role="status" aria-live="polite" className="sr-only" data-testid="ik-status">
          {busy ? "Generating drafts" :
            result ? `Import complete: ${result.state}` :
            error ? `Import failed: ${error}` : ""}
        </div>
      </form>

      {error && (
        <div role="alert" data-testid="ik-error" className="mt-6 max-w-2xl p-4 border border-red-300 bg-red-50 text-sm text-red-800 rounded-sm flex items-start gap-3">
          <AlertTriangle size={16} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {result && <ResultBlock result={result} />}
    </div>
  );
}


function ResultBlock({ result }) {
  const isOk = result.state === "ready_for_review";
  return (
    <section data-testid="ik-result" className="mt-8 max-w-2xl">
      <div className={`p-4 border rounded-sm ${
        isOk ? "border-[var(--ed-emerald)] bg-[var(--ed-emerald-tint)]"
             : "border-amber-400 bg-amber-50"}`}
      >
        <div className="flex items-center gap-2 mb-1">
          {isOk ? <CheckCircle2 size={16} className="text-[var(--ed-emerald)]" /> : <AlertTriangle size={16} className="text-amber-800" />}
          <p className="font-medium">
            {isOk ? `Ready for review — ${result.createdDrafts.length} draft${result.createdDrafts.length === 1 ? "" : "s"} created`
                  : "Import completed with issues"}
          </p>
        </div>
        <p className="text-sm">{result.notice}</p>
        <p className="text-[0.7rem] text-[var(--ed-ink-muted)] mt-2 font-mono">
          Job: {result.jobId} · Correlation: {result.correlationId}
        </p>
      </div>

      {result.sensitiveContentExcluded && (
        <div className="mt-4 p-3 border border-[var(--ed-rule)] bg-white text-sm rounded-sm flex items-start gap-2">
          <Info size={16} className="text-[var(--ed-ink-muted)] mt-0.5" />
          <span>
            <strong>{result.sensitiveExclusionCount}</strong> sensitive value(s)
            were excluded from generation. Manage credentials through the
            existing approved process.
          </span>
        </div>
      )}

      {isOk && result.createdDrafts.length > 0 && (
        <div className="mt-4">
          <p className="ed-overline text-[0.55rem] mb-1.5">Created drafts</p>
          <ul className="border border-[var(--ed-rule)] rounded-sm bg-white divide-y divide-[var(--ed-rule)]">
            {result.createdDrafts.map((d) => (
              <li key={d.itemId} className="p-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">{d.title}</p>
                  <p className="text-[0.65rem] font-mono text-[var(--ed-ink-muted)]">
                    {d.category} · {d.itemId}
                  </p>
                </div>
                <a
                  href={`/admin#/knowledge-base?item=${encodeURIComponent(d.itemId)}`}
                  data-testid={`ik-open-${d.itemId}`}
                  className="text-[0.7rem] uppercase tracking-[0.2em] px-3 py-1.5 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] rounded-sm inline-flex items-center gap-1.5"
                >
                  <BookOpen size={12} /> Review
                  <ExternalLink size={11} className="opacity-60" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isOk && result.createdDrafts.length === 0 && (
        <p data-testid="ik-empty" className="mt-4 text-sm text-[var(--ed-ink-muted)] italic">
          No supported article could be extracted from this message. See the
          notes below for what was omitted or flagged.
        </p>
      )}

      {result.warnings?.length > 0 && (
        <div className="mt-4">
          <p className="ed-overline text-[0.55rem] mb-1.5">Generation notes</p>
          <ul className="text-sm space-y-1" data-testid="ik-warnings">
            {result.warnings.map((w, i) => (
              <li key={i} className="flex items-center gap-2">
                <AlertTriangle size={12} className="text-amber-700 shrink-0" />
                <span><b className="font-mono text-[0.75rem]">{w.code}</b> — {w.topicLabel}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.omittedTopics?.length > 0 && (
        <div className="mt-4">
          <p className="ed-overline text-[0.55rem] mb-1.5">Omitted topics</p>
          <ul className="text-sm space-y-1" data-testid="ik-omissions">
            {result.omittedTopics.map((o, i) => (
              <li key={i} className="flex items-center gap-2 text-[var(--ed-ink-muted)]">
                <Info size={12} className="shrink-0" />
                <span>{o.topicLabel} — no article was created for this topic.</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.errorCode && (
        <div className="mt-4 p-3 border border-red-300 bg-red-50 text-sm rounded-sm" data-testid="ik-error-block">
          <p><b>{result.errorCode}</b> — {result.errorSummary}</p>
        </div>
      )}
    </section>
  );
}
